import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { Typography } from '@mui/material';
import {
  Asterisk,
  CircleCheck,
  CircleDashed,
  Trash2,
  Undo2,
  UserRound,
  UserRoundCheck,
} from 'lucide-react';
import { AlineAvatar, AlineTooltip } from 'src/components';
import type { AssigneeRolePlaceholder } from 'src/components/AssignRoleRow';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { AssignRolesButton } from 'src/components/workflow/AssignRolesButton';
import { LAUNCHER_PLACEHOLDER_TEXT } from 'src/components/workflow/workflow-helpers';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  getPermissionAssigneeDisplayDetails,
  getPermissionAvatarInfo,
  getPermissionUser,
} from 'src/helpers';
import { useSetTaskAssignees } from 'src/hooks';
import { getIsRolePermission } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import type { AssigneeRowMenuItem } from './AssigneeRowMenu';
import { ASSIGNEE_ROW_MENU_HOVER, AssigneeRowMenu } from './AssigneeRowMenu';
import { ReviewerRowActions } from './ReviewerRowActions';
import {
  getAssignmentRoleText,
  getHasReviewBegun,
  getHasReviewers,
  getIsReviewerRow,
  getIsReviewPending,
  getRequesterAssignment,
  getReviewRequestedText,
  REVIEWER_NEEDS_ASSIGNEE_REASON,
  sortAssignmentsByRole,
} from './task-review-helpers';
import { AssignmentStateIcon, NeutralPill } from './TaskStatusPill';
import { useTaskReviewActions } from './use-task-review-actions';
import { toTaskAssigneeInput } from '../task-assignee-write-plan';

const useStyles = makeStyles()(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
  },
  empty: {
    color: theme.appColors.iconsTypeMuted,
    fontSize: 14,
    padding: theme.spacing(1, 0),
    textAlign: 'center',
  },
  headerLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  headerTitle: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    minHeight: 24,
  },
  identity: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  name: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 600,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameGroup: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  picker: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
  },
  requiredIcon: {
    color: theme.palette.error.main,
    flexShrink: 0,
  },
  roleLabel: {
    color: theme.appColors.iconsType,
    fontSize: 12,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  row: {
    ...ASSIGNEE_ROW_MENU_HOVER,
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  trailing: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
}));

type ReviewTask = Pick<
  Apollo.TaskReviewFieldsFragment,
  | 'assignments'
  | 'counterpartyName'
  | 'hasExternalAssignees'
  | 'id'
  | 'isDelegatedToAi'
  | 'requesterIsInternal'
  | 'round'
  | 'status'
> & { completedAt: string | null; title: string };

export interface TaskReviewAssignmentsHandle {
  /** Scrolls the card into view. */
  reveal: () => void;
}

interface TaskReviewAssignmentsProps {
  /** Rendered first in the list when Aline AI is assigned. */
  aiRow: React.ReactNode;
  /** The button that adds and removes assignees; null when the viewer can't change them. */
  assigneePicker: React.ReactNode;
  // Why the viewer can't act on the task right now, such as a preview; disables the reviewer actions.
  changeStatusDisabledReason: string | null;
  editDisabledReason: string | null;
  isReadOnly: boolean;
  task: ReviewTask;
  workflowId: UUID;
  /** Sits in the header, left of the assignee picker. */
  headerAction?: React.ReactNode;
  /** Replaces the "Assignees" label, e.g. with the task page's card title. */
  heading?: React.ReactNode;
  ref?: React.Ref<TaskReviewAssignmentsHandle>;
}

/** The task's assignees: who they are, each one's standing decision and whether the task waits for them. */
export const TaskReviewAssignments = React.memo(function TaskReviewAssignments({
  aiRow,
  assigneePicker,
  changeStatusDisabledReason,
  editDisabledReason,
  headerAction,
  heading,
  isReadOnly,
  ref,
  task,
  workflowId,
}: TaskReviewAssignmentsProps) {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [withdrawFrom, setWithdrawFrom] = useState<{ name: string; permissionId: UUID } | null>(
    null
  );
  useImperativeHandle(
    ref,
    () => ({
      reveal: () => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    }),
    []
  );
  const { completeFor, setRequired, setRole, undoReviewRequest } = useTaskReviewActions(task.id);
  const canEditRequired = task.requesterIsInternal && !isReadOnly;
  const isOpen =
    task.status !== Apollo.TaskStatus.Completed && task.status !== Apollo.TaskStatus.Rejected;
  const canManageRows = canEditRequired && !editDisabledReason;
  const canCompleteForOthers = canManageRows && isOpen;
  const isAssigneeViewer =
    getRequesterAssignment(task.assignments, currentUserId)?.role ===
    Apollo.TaskAssigneeRole.Assignee;
  // Once review has begun, an assignee asks, reminds and withdraws one reviewer at a time.
  const canAskReviewers =
    isOpen && !isReadOnly && isAssigneeViewer && getHasReviewBegun(task.assignments);
  const canWithdraw = isOpen && !isReadOnly && (isAssigneeViewer || canManageRows);
  const { setTaskAssignees } = useSetTaskAssignees();
  const assignments = sortAssignmentsByRole(task.assignments);
  const hasReviewers = getHasReviewers(task.assignments);
  const assigneeRowCount = task.assignments.filter(
    assignment => !getIsReviewerRow(assignment)
  ).length;
  const handleRemove = useCallback(
    (permissionId: UUID) =>
      void setTaskAssignees({
        input: {
          assignees: task.assignments.flatMap(item =>
            item.permission.id === permissionId ? [] : [toTaskAssigneeInput(item.permission)]
          ),
          taskId: task.id,
        },
        workflowId,
      }),
    [setTaskAssignees, task.assignments, task.id, workflowId]
  );

  const hasRows = Boolean(aiRow) || task.assignments.length > 0;
  if (!hasRows && !assigneePicker) return null;

  return (
    <div ref={cardRef} className={classes.card}>
      <div className={classes.headerTitle}>
        {heading ?? <span className={classes.headerLabel}>Assignees</span>}
        {task.round > 1 && <NeutralPill label={`Round ${task.round}`} />}
        {Boolean(headerAction || assigneePicker) && (
          <div className={classes.picker}>
            {headerAction}
            {assigneePicker}
          </div>
        )}
      </div>
      <div className={classes.list}>
        {!hasRows && <Typography className={classes.empty}>No assignees</Typography>}
        {aiRow}
        {assignments.map(assignment => {
          const { permission } = assignment;
          const displayDetails = getPermissionAssigneeDisplayDetails(permission);
          const user = getPermissionUser(permission);
          const isOwnRow = user?.id === currentUserId;
          const isReviewer = getIsReviewerRow(assignment);
          // With reviewers, an assignee row records nothing, so only a reviewer can be marked done.
          const canCompleteFor =
            canCompleteForOthers &&
            !isOwnRow &&
            (isReviewer || !hasReviewers) &&
            assignment.decision !== Apollo.TaskAssigneeDecision.Completed;
          // The last one doing the work can't leave while reviewers wait on them.
          const holdsAssigneeSide =
            !isReviewer && hasReviewers && !task.isDelegatedToAi && assigneeRowCount === 1;
          const roleText = getPlaceholderRoleText(
            getAssignmentRoleText({
              hasReviewers,
              isExternal: assignment.isExternal,
              role: assignment.role,
            }),
            {
              isLauncher: permission.isLauncher,
              isUnfilledRole: getIsRolePermission(permission),
            }
          );
          const isPending = getIsReviewPending(assignment);
          const unfilledRole =
            getIsRolePermission(permission) && permission.roleName
              ? {
                  avatarColor: permission.avatarColor,
                  permissionId: permission.id,
                  roleName: permission.roleName,
                }
              : null;
          const withdraw =
            canWithdraw && isPending
              ? [
                  {
                    icon: Undo2,
                    label: 'Undo review request',
                    onClick: () =>
                      setWithdrawFrom({
                        name: displayDetails.primary,
                        permissionId: permission.id,
                      }),
                  },
                ]
              : [];
          const menuItems = canManageRows
            ? [
                ...withdraw,
                ...getRowMenuItems({
                  canCompleteFor,
                  holdsAssigneeSide,
                  isRequired: assignment.isRequired,
                  isReviewer,
                  onCompleteFor: () => void completeFor(permission.id),
                  onRemove: () => handleRemove(permission.id),
                  onSwitchRole: () =>
                    void setRole(
                      permission.id,
                      isReviewer
                        ? Apollo.TaskAssigneeRole.Assignee
                        : Apollo.TaskAssigneeRole.Reviewer
                    ),
                  onToggleRequired: () => void setRequired(permission.id, !assignment.isRequired),
                }),
              ]
            : withdraw;
          return (
            <div key={permission.id} className={classes.row}>
              <AlineAvatar
                avatarInfo={getPermissionAvatarInfo(permission)}
                styles={{ backgroundColor: displayDetails.avatarColor, size: 'xs' }}
              />
              <div className={classes.identity}>
                <div className={classes.nameGroup}>
                  <Typography className={classes.name} title={user?.email}>
                    {displayDetails.primary}
                  </Typography>
                  {assignment.isRequired && (
                    <AlineTooltip
                      arrow
                      placement="top"
                      title="Required: Complete waits for their approval"
                    >
                      <Asterisk aria-label="Required" className={classes.requiredIcon} size={16} />
                    </AlineTooltip>
                  )}
                </div>
                {Boolean(roleText) && (
                  <Typography className={classes.roleLabel} title={roleText ?? undefined}>
                    {roleText}
                  </Typography>
                )}
              </div>
              {/* The menu leads, so the state icons line up at the edge whether a row has one or not. */}
              <div className={classes.trailing}>
                {!isReadOnly && isOpen && (
                  <AssignRoleLink role={unfilledRole} workflowId={workflowId} />
                )}
                {menuItems.length > 0 && (
                  <AssigneeRowMenu items={menuItems} name={displayDetails.primary} />
                )}
                {canAskReviewers && isReviewer && (
                  <ReviewerRowActions
                    disabledReason={changeStatusDisabledReason}
                    hasAnswered={Boolean(assignment.decision)}
                    isPending={isPending}
                    name={displayDetails.primary}
                    permissionId={permission.id}
                    taskId={task.id}
                  />
                )}
                <AssignmentStateIcon
                  decidedAt={assignment.decidedAt}
                  decision={assignment.decision}
                  hasReviewers={hasReviewers}
                  name={displayDetails.primary}
                  requestedText={
                    isReviewer ? getReviewRequestedText(assignment, currentUserId) : null
                  }
                  reviewRequestedAt={assignment.reviewRequestedAt}
                  role={assignment.role}
                  taskCompletedAt={task.completedAt}
                />
              </div>
            </div>
          );
        })}
      </div>
      <ConfirmationDialog
        message={`${withdrawFrom?.name ?? 'This reviewer'} will no longer be asked to review.`}
        open={Boolean(withdrawFrom)}
        primaryText="Undo Request"
        title="Undo review request?"
        onClickPrimary={() => {
          if (withdrawFrom) void undoReviewRequest(withdrawFrom.permissionId);
          setWithdrawFrom(null);
        }}
        onClose={() => setWithdrawFrom(null)}
      />
    </div>
  );
});

function AssignRoleLink({
  role,
  workflowId,
}: {
  role: AssigneeRolePlaceholder | null;
  workflowId: UUID;
}): JSX.Element | null {
  if (!role) return null;
  return (
    <AssignRolesButton
      dialogTitle={`Assign ${role.roleName}`}
      label="Assign"
      permissionIds={[role.permissionId]}
      workflowId={workflowId}
    />
  );
}

/**
 * A placeholder says what fills it, after its side of the task when there is one: a role nobody
 * holds yet is unassigned, and the launcher is filled when the template launches.
 */
function getPlaceholderRoleText(
  roleText: string | null,
  options: { isLauncher: boolean; isUnfilledRole: boolean }
): string | null {
  const placeholderText = getPlaceholderText(options);
  if (!placeholderText) return roleText;
  return roleText ? `${roleText} · ${placeholderText}` : placeholderText;
}

function getPlaceholderText(options: { isLauncher: boolean; isUnfilledRole: boolean }) {
  if (options.isLauncher) return LAUNCHER_PLACEHOLDER_TEXT;
  if (options.isUnfilledRole) return 'Unassigned role';
  return null;
}

function getRowMenuItems(options: {
  canCompleteFor: boolean;
  /** The row is the task's last assignee while it has reviewers. */
  holdsAssigneeSide: boolean;
  isRequired: boolean;
  isReviewer: boolean;
  onCompleteFor: () => void;
  onRemove: () => void;
  onSwitchRole: () => void;
  onToggleRequired: () => void;
}): AssigneeRowMenuItem[] {
  const disabledReason = options.holdsAssigneeSide ? REVIEWER_NEEDS_ASSIGNEE_REASON : null;
  const toggleRequired = options.isRequired
    ? { icon: CircleDashed, label: 'Mark optional', onClick: options.onToggleRequired }
    : {
        detail: "The task can't be completed until they approve",
        icon: Asterisk,
        label: 'Mark required',
        onClick: options.onToggleRequired,
      };
  // Only a reviewer can be required: any one assignee finishes the task.
  const reviewerItems = options.isReviewer ? [toggleRequired] : [];
  const switchRole = options.isReviewer
    ? { icon: UserRound, label: 'Make assignee', onClick: options.onSwitchRole }
    : {
        disabledReason,
        icon: UserRoundCheck,
        label: 'Make reviewer',
        onClick: options.onSwitchRole,
      };
  const markComplete = {
    icon: CircleCheck,
    label: 'Mark complete',
    onClick: options.onCompleteFor,
  };
  const remove = {
    disabledReason,
    icon: Trash2,
    isDestructive: true,
    label: 'Remove',
    onClick: options.onRemove,
  };
  return [switchRole, ...(options.canCompleteFor ? [markComplete] : []), ...reviewerItems, remove];
}
